// src/pages/PlayoffPage.jsx
// Visual playoff bracket: seeds from standings, SF results feed into Final.
import React, { useMemo } from "react";

function computeStandings(schedule) {
  const map = new Map();
  const ensure = t => { if (t && !map.has(t)) map.set(t, { w:0,l:0,t:0,pf:0,pa:0 }); };
  Object.keys(schedule || {}).map(Number).sort((a,b)=>a-b).forEach(wk => {
    (schedule[String(wk)]?.matchups || []).forEach(m => {
      const hp = Number(m.homePoints), ap = Number(m.awayPoints);
      if (!m.home||!m.away||!Number.isFinite(hp)||!Number.isFinite(ap)) return;
      ensure(m.home); ensure(m.away);
      const a = map.get(m.home), b = map.get(m.away);
      a.pf+=hp; a.pa+=ap; b.pf+=ap; b.pa+=hp;
      if (hp>ap){a.w++;b.l++;} else if(ap>hp){b.w++;a.l++;} else{a.t++;b.t++;}
    });
  });
  return [...map.entries()]
    .map(([team,r])=>({team,...r}))
    .sort((a,b)=> b.w-a.w || b.t-a.t || b.pf-a.pf || a.pa-b.pa);
}

function MatchupBox({ home, away, homePoints, awayPoints, label, isLive }) {
  const hasScore = homePoints !== null && awayPoints !== null;
  const homeWin  = hasScore && homePoints > awayPoints;
  const awayWin  = hasScore && awayPoints > homePoints;

  return (
    <div style={{
      background: "var(--bg3)", border: "1px solid var(--border2)",
      borderRadius: "var(--radius)", overflow: "hidden", minWidth: 220,
      boxShadow: isLive ? "0 0 0 1px var(--gold), 0 4px 20px var(--gold-glow)" : "none",
    }}>
      {label && (
        <div style={{
          padding: "4px 12px", fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
          textTransform: "uppercase", background: "var(--bg2)", color: "var(--text-muted)",
          borderBottom: "1px solid var(--border)",
        }}>{label}</div>
      )}
      {[{ team: home, pts: homePoints, win: homeWin }, { team: away, pts: awayPoints, win: awayWin }].map((side, i) => (
        <div key={i} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 14px",
          borderBottom: i === 0 ? "1px solid var(--border)" : "none",
          background: side.win ? "rgba(245,166,35,0.07)" : "transparent",
        }}>
          <div style={{ fontWeight: side.win ? 700 : 500, fontSize: 14,
            color: side.win ? "var(--gold)" : side.team ? "var(--text)" : "var(--text-muted)",
            fontStyle: side.team ? "normal" : "italic",
          }}>
            {side.team || "TBD"}
          </div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 20, fontWeight: 700,
            color: side.win ? "var(--gold)" : hasScore ? "var(--text-dim)" : "var(--text-muted)",
          }}>
            {side.pts !== null && side.pts !== undefined ? side.pts : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function SeedRow({ rank, team, record }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 14px", borderBottom: "1px solid var(--border)",
    }}>
      <div className={`rank-badge ${rank <= 3 ? `rank-${rank}` : ""}`} style={{ width: 24, height: 24, fontSize: 11, flexShrink: 0 }}>
        {rank}
      </div>
      <div style={{ flex: 1, fontWeight: rank <= 4 ? 700 : 400, fontSize: 13 }}>{team}</div>
      <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
        {record.w}-{record.l}{record.t ? `-${record.t}` : ""}
        <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>{record.pf} pts</span>
      </div>
      {rank <= 4 && <span className="pill green" style={{ fontSize: 8 }}>PO</span>}
    </div>
  );
}

export function PlayoffPage({ league }) {
  const schedule = useMemo(() => league?.season?.schedule || {}, [league]);
  const standings = useMemo(() => computeStandings(schedule), [schedule]);

  const seeds = standings.slice(0, 4);
  const seed  = (n) => seeds[n-1]?.team || "TBD";

  // SF matchups from schedule week 13
  const sf1 = useMemo(() => (schedule["13"]?.matchups || [])[0] || null, [schedule]);
  const sf2 = useMemo(() => (schedule["13"]?.matchups || [])[1] || null, [schedule]);

  // Final from schedule week 14
  const final = useMemo(() => (schedule["14"]?.matchups || [])[0] || null, [schedule]);

  // Determine SF winners
  const sf1Winner = sf1?.homePoints > sf1?.awayPoints ? sf1.home
    : sf1?.awayPoints > sf1?.homePoints ? sf1.away : null;
  const sf2Winner = sf2?.homePoints > sf2?.awayPoints ? sf2.home
    : sf2?.awayPoints > sf2?.homePoints ? sf2.away : null;

  const champion = final?.homePoints > final?.awayPoints ? final.home
    : final?.awayPoints > final?.homePoints ? final.away : null;

  const phase = league?.season?.phase || "regular";
  const currentWeek = league?.season?.week || 1;
  const inSemis  = currentWeek === 13;
  const inFinal  = currentWeek === 14;
  const complete = phase === "complete" || (currentWeek > 14);

  if (standings.length < 2) {
    return (
      <div className="panel" style={{ padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🏆</div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Playoffs</div>
        <div style={{ color: "var(--text-dim)", fontSize: 13 }}>
          Playoff bracket will appear here after the regular season ends.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Champion banner */}
      {champion && (
        <div style={{
          background: "linear-gradient(135deg, rgba(245,166,35,0.15) 0%, rgba(245,166,35,0.05) 100%)",
          border: "1px solid rgba(245,166,35,0.4)",
          borderRadius: "var(--radius-lg)", padding: "20px 24px", marginBottom: 16,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 36 }}>🏆</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 900, color: "var(--gold)", marginTop: 8 }}>
            {champion}
          </div>
          <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 4 }}>IPL Fantasy Champion</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 16 }}>
        {/* Bracket */}
        <div>
          <div style={{ fontWeight: 700, marginBottom: 12, color: "var(--text-dim)",
            letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 11 }}>
            Bracket
          </div>

          <div style={{ display: "flex", gap: 0, alignItems: "center" }}>
            {/* Semis column */}
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              <MatchupBox
                label="Semifinal 1 · #1 vs #4"
                home={sf1?.home || seed(1)}
                away={sf1?.away || seed(4)}
                homePoints={sf1?.homePoints ?? null}
                awayPoints={sf1?.awayPoints ?? null}
                isLive={inSemis}
              />
              <MatchupBox
                label="Semifinal 2 · #2 vs #3"
                home={sf2?.home || seed(2)}
                away={sf2?.away || seed(3)}
                homePoints={sf2?.homePoints ?? null}
                awayPoints={sf2?.awayPoints ?? null}
                isLive={inSemis}
              />
            </div>

            {/* Connector lines SVG */}
            <svg width="60" height="200" style={{ flexShrink: 0 }}>
              <path d="M0,52 H30 V148 H0" fill="none" stroke="var(--border2)" strokeWidth="1.5"/>
              <path d="M30,100 H60" fill="none" stroke="var(--border2)" strokeWidth="1.5"/>
            </svg>

            {/* Final column */}
            <div>
              <MatchupBox
                label="Grand Final"
                home={final?.home || sf1Winner || "SF1 Winner"}
                away={final?.away || sf2Winner || "SF2 Winner"}
                homePoints={final?.homePoints ?? null}
                awayPoints={final?.awayPoints ?? null}
                isLive={inFinal}
              />
              {complete && champion && (
                <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "var(--gold)", fontWeight: 700 }}>
                  🏆 Champion
                </div>
              )}
            </div>
          </div>

          {!inSemis && !inFinal && !complete && (
            <div style={{
              marginTop: 16, padding: "10px 14px",
              background: "var(--bg3)", border: "1px solid var(--border)",
              borderRadius: "var(--radius)", fontSize: 13, color: "var(--text-dim)",
            }}>
              Playoffs start Week 13 (Semifinals) → Week 14 (Final). Top 4 teams qualify.
            </div>
          )}
        </div>

        {/* Seeds / Standings */}
        <div className="panel" style={{ alignSelf: "start" }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>Playoff Seeds</div>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>From standings</span>
          </div>
          {standings.map((s, i) => (
            <SeedRow
              key={s.team}
              rank={i + 1}
              team={s.team}
              record={s}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
